import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { Sort, MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
  InfoNotification,
  InfoNotificationsService,
} from 'src/app/services/info-notifications/info-notifications.service';
import { CreateInfoNotificationDialogComponent } from './create-info-notification-dialog/create-info-notification-dialog.component';
import {
  EditInfoNotificationDialogComponent,
  EditInfoNotificationDialogData,
} from './edit-info-notification-dialog/edit-info-notification-dialog.component';
import {
  DeleteInfoNotificationDialogComponent,
  DeleteInfoNotificationDialogData,
} from './delete-info-notification-dialog/delete-info-notification-dialog.component';
import { AxiosError } from 'axios';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeDropzoneComponent } from '../../../shared/payee-dropzone/payee-dropzone.component';
import { PayeeMiniIconButtonComponent } from '../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { MatTooltip } from '@angular/material/tooltip';
import { KeyValuePipe } from '@angular/common';
import { CountryFlagPipe } from '../../../shared/pipes/country-flag/country-flag.pipe';

@Component({
  selector: 'app-info-notifications',
  templateUrl: './info-notifications.component.html',
  styleUrls: ['./info-notifications.component.scss'],
  imports: [
    PayeeFormFieldComponent,
    PayeeInputDirective,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinner,
    PayeeDropzoneComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeButtonComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    KeyValuePipe,
    CountryFlagPipe,
  ],
})
export class InfoNotificationsComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private snackbar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private infoNotificationsService = inject(InfoNotificationsService);

  private readonly destroy = new Subject<void>();

  readonly nameFormControl = this.fb.control<string | null>(
    '',
    Validators.required,
  );
  readonly payeeCaseReferenceId = this.fb.control<string | null>('');
  files: File[] = [];

  readonly dataSource = new MatTableDataSource<InfoNotification>();

  tableLoading = false;
  previewLoading = new Set<number>(); // Track loading state for each notification
  readonly displayedColumns = [
    'name',
    'languages',
    'preview',
    'edit',
    'delete',
  ] as const;

  pageSize = 10;
  pageIndex = 0;
  length = 0;

  sort_by?: string;
  sort_direction: 'asc' | 'desc' = 'asc';

  async ngOnInit(): Promise<void> {
    await this.loadNotifications();

    this.nameFormControl.valueChanges
      .pipe(takeUntil(this.destroy), debounceTime(1000), distinctUntilChanged())
      .subscribe({
        next: async () => {
          this.pageIndex = 0;
          await this.loadNotifications();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async pageChange(event: PageEvent) {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
    }
    if (event.pageIndex !== this.pageIndex) {
      this.pageIndex = event.pageIndex;
    }
    await this.loadNotifications();
  }

  async sortData(event: Sort): Promise<void> {
    this.sort_by = event.active;
    this.sort_direction = event.direction || 'asc';
    await this.loadNotifications();
  }

  async loadNotifications(): Promise<void> {
    this.tableLoading = true;

    try {
      const response = await this.infoNotificationsService.getInfoNotifications(
        this.pageIndex + 1,
        this.pageSize,
        this.nameFormControl.value!,
        this.sort_by,
        this.sort_direction,
      );

      if (response) {
        this.dataSource.data = response.data;
        this.length = response.pagination.total;
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Handle error appropriately
    } finally {
      this.tableLoading = false;
    }
  }

  createNotification(): void {
    const dialogRef = this.dialog.open<
      CreateInfoNotificationDialogComponent,
      void,
      InfoNotification
    >(CreateInfoNotificationDialogComponent, {
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dataSource.data.push(result);
        this.dataSource._updateChangeSubscription();
        this.length++;
      }
    });
  }

  editNotification(notification: InfoNotification): void {
    const dialogRef = this.dialog.open<
      EditInfoNotificationDialogComponent,
      EditInfoNotificationDialogData,
      InfoNotification
    >(EditInfoNotificationDialogComponent, {
      data: { notification },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        Object.assign(notification, result);
      }
    });
  }

  deleteNotification(notification: InfoNotification): void {
    const dialogRef = this.dialog.open<
      DeleteInfoNotificationDialogComponent,
      DeleteInfoNotificationDialogData,
      boolean
    >(DeleteInfoNotificationDialogComponent, {
      data: { notification },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Remove the deleted notification from the table data
        const index = this.dataSource.data.findIndex(
          (n) => n.id === notification.id,
        );
        if (index !== -1) {
          this.dataSource.data.splice(index, 1);
          this.dataSource._updateChangeSubscription();
          this.length--;
        }
      }
    });
  }

  filesDropped(files: FileList): void {
    for (let i = 0; i < files.length; ++i) {
      this.files.push(files.item(i)!);
    }
  }

  async previewNotification(notification: InfoNotification): Promise<void> {
    if (!this.payeeCaseReferenceId.value) {
      return;
    }

    // Send preview email to current user
    this.previewLoading.add(notification.id);

    try {
      await this.infoNotificationsService.previewInfoNotification(
        notification.id,
        this.payeeCaseReferenceId.value,
        this.files,
      );
    } catch (error) {
      console.error('Error sending preview email:', error);
      let message = 'Nem sikerült az email küldés';
      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }
      this.snackbar.open(message, 'OK', { duration: 10_000 });
    } finally {
      this.previewLoading.delete(notification.id);
    }
  }
}
